import { Alert, Progress, Upload, type UploadProps } from 'antd';
import { UPLOAD_CONTENT_TYPES } from '@chukua/common';
import { FileText, UploadCloud } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useUpload } from '../hooks/use-upload';
import type { UploadView } from '../uploads.api';

const ACCEPT = UPLOAD_CONTENT_TYPES.join(',');

interface AntUploadCallbacks {
  onProgress?: (event: { percent: number }) => void;
  onSuccess?: (body: unknown) => void;
  onError?: (error: Error) => void;
}

interface FilePreviewProps {
  file: File;
}

/** Client-side only — no derivative is generated server-side, per the ticket's scope. */
function FilePreview({ file }: FilePreviewProps) {
  const url = useMemo(() => URL.createObjectURL(file), [file]);
  useEffect(() => () => URL.revokeObjectURL(url), [url]);

  if (file.type === 'application/pdf') {
    return (
      <a href={url} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 text-sm text-link">
        <FileText className="size-4" /> View PDF
      </a>
    );
  }
  return <img src={url} alt={file.name} className="h-16 w-16 rounded-md border border-line object-cover" />;
}

interface FileUploadProps {
  entityType: string;
  entityId: string;
  onUploaded?: (upload: UploadView) => void;
}

/** Presigned upload straight to object storage (gitaumoses4/chukua#16): drag-drop, progress, image/PDF preview. */
export function FileUpload({ entityType, entityId, onUploaded }: FileUploadProps) {
  const { state, start } = useUpload(entityType, entityId);
  const [file, setFile] = useState<File>();
  const callbacksRef = useRef<AntUploadCallbacks>({});
  const onUploadedRef = useRef(onUploaded);
  useEffect(() => {
    onUploadedRef.current = onUploaded;
  });

  useEffect(() => {
    if (state.status === 'uploading') callbacksRef.current.onProgress?.({ percent: state.progress });
    if (state.status === 'done') {
      callbacksRef.current.onSuccess?.(state.upload);
      onUploadedRef.current?.(state.upload);
    }
    if (state.status === 'failed') callbacksRef.current.onError?.(new Error(state.error));
    // `state` alone drives this: `onUploaded` is read from a ref so a consumer that stores the
    // completed upload in its own state (creating a new `onUploaded` each render) can't retrigger it.
  }, [state]);

  const customRequest: UploadProps['customRequest'] = (options) => {
    const uploaded = options.file as File;
    callbacksRef.current = { onProgress: options.onProgress, onSuccess: options.onSuccess, onError: options.onError };
    setFile(uploaded);
    void start(uploaded);
  };

  const busy = state.status === 'uploading' || state.status === 'confirming';

  return (
    <div className="flex flex-col gap-3">
      <Upload.Dragger accept={ACCEPT} multiple={false} disabled={busy} showUploadList={false} customRequest={customRequest}>
        <p className="flex items-center justify-center gap-2 text-sm text-muted">
          <UploadCloud className="size-4" /> Drag a file here, or click to browse
        </p>
      </Upload.Dragger>

      {state.status === 'uploading' && <Progress percent={state.progress} size="small" />}
      {state.status === 'confirming' && <Progress percent={100} size="small" status="active" />}
      {state.status === 'failed' && <Alert type="error" message={state.error} showIcon />}
      {file && <FilePreview file={file} />}
    </div>
  );
}
